import { useQuery } from '@tanstack/react-query'
import { SidebarProvider } from '@/components/ui/sidebar'
import { AppSidebar } from '@/components/layout/AppSidebar'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { getAuthHeaders } from '@/contexts/AuthContext'
import {
  Truck,
  TrendingUp,
  AlertTriangle,
  Shield,
  Wrench,
  Activity,
  Clock,
} from 'lucide-react'
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'

// DEMO MODE: Use mock data (set to false once API authentication is configured)
const USE_MOCK_DATA = false;

// Mock data for demo
const mockFleetStats = {
  total: 48,
  active: 35,
  idle: 8,
  offline: 5,
  totalDistance: 12847,
  avgSpeed: 58,
  efficiency: 85
};

const mockAlerts = [
  { id: 1, severity: 'critical', message: 'Vehicle #142 - Engine temperature high', time: '2 min ago' },
  { id: 2, severity: 'warning', message: 'Driver #23 approaching HOS limit', time: '15 min ago' },
  { id: 3, severity: 'info', message: 'Maintenance due: Vehicle #089', time: '1 hour ago' }
];

const mockSafetyData = {
  score: 87,
  incidents: 12,
  trend: 'up'
};

const mockHealthData = {
  critical: 2,
  warning: 7,
  good: 39
};

export function Dashboard() {
  // Fetch fleet stats
  const { data: fleetStats } = useQuery({
    queryKey: ['fleet-stats'],
    queryFn: async () => {
      if (USE_MOCK_DATA) return mockFleetStats;
      const res = await fetch('/api/fleet/stats', {
        headers: getAuthHeaders()
      })
      return res.json()
    },
    refetchInterval: USE_MOCK_DATA ? false : 10000,
  })

  // Fetch alerts
  const { data: alertsData } = useQuery({
    queryKey: ['alerts'],
    queryFn: async () => {
      if (USE_MOCK_DATA) return mockAlerts;
      const res = await fetch('/api/fleet/alerts?limit=5', {
        headers: getAuthHeaders()
      })
      return res.json()
    },
    refetchInterval: USE_MOCK_DATA ? false : 10000,
  })

  // Fetch safety metrics
  const { data: safetyData } = useQuery({
    queryKey: ['safety-metrics'],
    queryFn: async () => {
      if (USE_MOCK_DATA) return mockSafetyData;
      const res = await fetch('/api/safety/metrics', {
        headers: getAuthHeaders()
      })
      return res.json()
    },
    refetchInterval: USE_MOCK_DATA ? false : 60000,
  })

  // Fetch vehicle health
  const { data: healthData } = useQuery({
    queryKey: ['vehicle-health-summary'],
    queryFn: async () => {
      if (USE_MOCK_DATA) return mockHealthData;
      const res = await fetch('/api/maintenance/vehicle-health', {
        headers: getAuthHeaders()
      })
      return res.json()
    },
    refetchInterval: USE_MOCK_DATA ? false : 60000,
  })

  // Mock data for performance over time (replace with real API)
  const performanceData = [
    { date: 'Mon', distance: 1250, avgSpeed: 58, efficiency: 85 },
    { date: 'Tue', distance: 1420, avgSpeed: 62, efficiency: 88 },
    { date: 'Wed', distance: 1180, avgSpeed: 55, efficiency: 82 },
    { date: 'Thu', distance: 1580, avgSpeed: 65, efficiency: 90 },
    { date: 'Fri', distance: 1390, avgSpeed: 60, efficiency: 86 },
    { date: 'Sat', distance: 980, avgSpeed: 52, efficiency: 80 },
    { date: 'Sun', distance: 750, avgSpeed: 48, efficiency: 78 },
  ]

  const complianceData = [
    { name: 'Compliant', value: safetyData?.fleet_score >= 75 ? 85 : 70, color: '#10b981' },
    { name: 'At Risk', value: safetyData?.fleet_score >= 75 ? 10 : 20, color: '#f59e0b' },
    { name: 'Non-Compliant', value: safetyData?.fleet_score >= 75 ? 5 : 10, color: '#ef4444' },
  ]

  const needsAttention = healthData?.vehicles?.filter((v: any) => v.health_status === 'needs_attention').length || 0

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full">
        <AppSidebar />
        <main className="flex-1 overflow-auto p-6 bg-background">
          <div className="max-w-[1800px] mx-auto space-y-6">
            {/* Header */}
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
              <p className="text-muted-foreground">
                Fleet performance metrics and real-time insights
              </p>
            </div>

            {/* KPI Cards */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {/* Active Vehicles */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Active Vehicles</CardTitle>
                  <Truck className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{fleetStats?.active || 0}</div>
                  <p className="text-xs text-muted-foreground">
                    of {fleetStats?.total || 0} total
                  </p>
                  <div className="mt-2 flex gap-2">
                    <Badge variant="outline" className="text-xs">
                      {fleetStats?.idle || 0} idle
                    </Badge>
                    <Badge variant="destructive" className="text-xs">
                      {fleetStats?.offline || 0} offline
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Active Alerts */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Active Alerts</CardTitle>
                  <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{alertsData?.alerts?.length || 0}</div>
                  <p className="text-xs text-muted-foreground">
                    {alertsData?.alerts?.filter((a: any) => a.severity === 'critical').length || 0} critical
                  </p>
                  {alertsData?.alerts?.length > 0 && (
                    <div className="mt-2">
                      <Badge variant="destructive" className="text-xs">
                        Attention Required
                      </Badge>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Safety Score */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Fleet Safety Score</CardTitle>
                  <Shield className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{safetyData?.fleet_score || 0}</div>
                  <p className="text-xs text-muted-foreground">
                    {safetyData?.period_change > 0 ? '+' : ''}{safetyData?.period_change || 0} vs last period
                  </p>
                  <div className="mt-2">
                    <Badge
                      variant={(safetyData?.fleet_score >= 90) ? 'default' : (safetyData?.fleet_score >= 75) ? 'secondary' : 'destructive'}
                      className="text-xs"
                    >
                      {(safetyData?.fleet_score >= 90) ? 'Excellent' : (safetyData?.fleet_score >= 75) ? 'Good' : 'Needs Improvement'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Maintenance Status */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Maintenance Status</CardTitle>
                  <Wrench className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{needsAttention}</div>
                  <p className="text-xs text-muted-foreground">
                    vehicles need attention
                  </p>
                  <div className="mt-2">
                    <Badge
                      variant={needsAttention === 0 ? 'default' : 'destructive'}
                      className="text-xs"
                    >
                      {needsAttention === 0 ? 'All Good' : 'Action Required'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts Row 1 */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
              {/* Performance Trend */}
              <Card className="col-span-4">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Fleet Performance - Last 7 Days
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={performanceData}>
                      <defs>
                        <linearGradient id="colorDistance" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="distance"
                        stroke="#8884d8"
                        fillOpacity={1}
                        fill="url(#colorDistance)"
                        name="Distance (mi)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Compliance Status */}
              <Card className="col-span-3">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Compliance Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={complianceData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry: any) => `${entry.name}: ${entry.value}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {complianceData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Charts Row 2 */}
            <div className="grid gap-4 md:grid-cols-2">
              {/* Speed & Efficiency */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5" />
                    Speed & Efficiency Trends
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={performanceData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="avgSpeed"
                        stroke="#8884d8"
                        name="Avg Speed (mph)"
                      />
                      <Line
                        type="monotone"
                        dataKey="efficiency"
                        stroke="#82ca9d"
                        name="Efficiency %"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Recent Activity */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    Recent Alerts
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {alertsData?.alerts?.slice(0, 5).map((alert: any) => (
                      <div
                        key={alert.id}
                        className="flex items-start gap-3 p-3 rounded-lg border"
                      >
                        <AlertTriangle
                          className={`h-4 w-4 flex-shrink-0 mt-0.5 ${
                            alert.severity === 'critical'
                              ? 'text-red-500'
                              : 'text-yellow-500'
                          }`}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm">{alert.title}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {alert.vehicle_name} • {alert.description}
                          </p>
                        </div>
                        <Badge
                          variant={alert.severity === 'critical' ? 'destructive' : 'secondary'}
                          className="text-xs flex-shrink-0"
                        >
                          {alert.severity}
                        </Badge>
                      </div>
                    )) || (
                      <div className="text-center py-8 text-muted-foreground">
                        <Shield className="h-12 w-12 mx-auto mb-3 opacity-20" />
                        <p>No active alerts</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </main>
      </div>
    </SidebarProvider>
  )
}
